import React, { useState, useEffect, useRef } from 'react';
import { Node } from '@xyflow/react';
import { Input, Modal, TextArea } from 'common/components';
import {
    NodeConfigModalProps,
    NodeData,
    CallNodeData,
    DelayNodeData,
    LeadNodeData,
    SMSNodeData,
    SMSMode,
    TimeoutUnit,
    DecisionNodeData,
    WorkflowTagsType,
} from './WorkflowBuilder.types';
import { useGetPrompts } from 'services/hooks/queries';
import { getNodeTypeLabel } from './workflowNodeUtils';
import { NodeConfigCall } from './components';
import { NodeConfigLead, NodeConfigSms, NodeConfigDelay, NodeConfigDecision } from './components/NodeConfig';
import type { NodeConfigDecisionRef } from './components/NodeConfig/NodeConfigDecision';
import { FormProvider, useForm } from 'react-hook-form';

export type FormNodeConfigProps = {
    label: string;
    callText: string;
    workflowTagsType: WorkflowTagsType;
    aiPrompt: string;
    promptId: string;
    promptName: string;
    leaveVoicemail: boolean;
    // SMS node fields
    smsMode: SMSMode;
    messageContent: string;
    langfusePromptId: string;
    userAbandonedTimeoutValue: number;
    agentAbandonedTimeoutValue: number;
    userAbandonedTimeoutUnit: TimeoutUnit;
    agentAbandonedTimeoutUnit: TimeoutUnit;
    expectResponse: boolean;
};

const NodeConfigModal: React.FC<NodeConfigModalProps> = ({
    node,
    nodes,
    terminalDecisionNodeId,
    onClose,
    onSave,
    onDecisionDone,
}) => {
    const decisionConfigRef = useRef<NodeConfigDecisionRef>(null);
    // Node-specific states
    const [leadStatus, setLeadStatus] = useState<string>(
        'crmStatus' in node.data ? (node.data as LeadNodeData).crmStatus || '' : ''
    );

    const { prompts, isLoadingPrompts } = useGetPrompts();

    // For delayNode
    const delayData = 'delayType' in node.data ? (node.data as DelayNodeData) : null;
    const [delayType, setDelayType] = useState<'TIMESTAMP' | 'DURATION'>(delayData?.delayType || 'TIMESTAMP');
    const [timeValue, setTimeValue] = useState<string>(delayData?.timeValue || '30');
    const [timeUnit, setTimeUnit] = useState<'minutes' | 'hours' | 'days'>(delayData?.timeUnit || 'minutes');
    const [specificDateTime, setSpecificDateTime] = useState<string>(
        delayData?.specificDateTime || new Date().toISOString()
    );

    const smsData = node.type === 'smsNode' ? (node.data as SMSNodeData) : null;

    const form = useForm<FormNodeConfigProps>({
        defaultValues: {
            label: node.data.label || '',
            workflowTagsType: WorkflowTagsType.PREDEFINED,
            aiPrompt: '',
            callText: 'callText' in node.data ? (node.data as CallNodeData).callText || '' : '',
            promptId: 'promptId' in node.data ? (node.data as CallNodeData).promptId || '' : '',
            promptName: 'promptName' in node.data ? (node.data as CallNodeData).promptName || '' : '',
            leaveVoicemail: 'leaveVoicemail' in node.data ? (node.data as CallNodeData).leaveVoicemail ?? false : false,
            smsMode: smsData?.smsMode || 'static',
            messageContent: smsData?.messageContent || '',
            langfusePromptId: smsData?.langfusePromptId || '',
            userAbandonedTimeoutUnit:
                smsData?.userAbandonedTimeoutUnit ??
                (smsData?.userAbandonedTimeoutMinutes != null ? 'minutes' : 'hours'),
            agentAbandonedTimeoutUnit:
                smsData?.agentAbandonedTimeoutUnit ??
                (smsData?.agentAbandonedTimeoutMinutes != null ? 'minutes' : 'hours'),
            userAbandonedTimeoutValue: smsData?.userAbandonedTimeoutMinutes ?? smsData?.userAbandonedTimeoutHours ?? 24,
            agentAbandonedTimeoutValue:
                smsData?.agentAbandonedTimeoutMinutes ?? smsData?.agentAbandonedTimeoutHours ?? 72,
            expectResponse: smsData?.expectResponse ?? true,
        },
    });

    // Initialize data from node
    useEffect(() => {
        if (node.type === 'delayNode') {
            const delayData = node.data as DelayNodeData;
            setDelayType(delayData.delayType || 'TIMESTAMP');
            setTimeValue(delayData.timeValue || '30');
            setTimeUnit(delayData.timeUnit || 'minutes');
            setSpecificDateTime(delayData.specificDateTime || new Date().toISOString());
        } else if (node.type === 'callNode') {
            const callData = node.data as CallNodeData;
            const displayName = callData.promptName || callData.aiPrompt || '';
            let promptIdValue = callData.promptId || '';

            if (!callData.promptId && displayName && prompts?.length) {
                const matchedPrompt = prompts.find((p) => p.prompt_name === displayName);
                if (matchedPrompt) {
                    promptIdValue = matchedPrompt.id;
                }
            }

            form.setValue('promptId', promptIdValue);
            form.setValue('promptName', displayName);
            form.setValue('callText', callData.callText || '');
            form.setValue('leaveVoicemail', callData.leaveVoicemail ?? false);
        }
    }, [node, prompts, form]);

    const handleSave = (data: FormNodeConfigProps) => {
        // Build updatedData based on node type with proper typing
        let updatedData: Partial<NodeData>;
        const label = data.label || getNodeTypeLabel(node);

        switch (node.type) {
            case 'leadNode':
                updatedData = {
                    label,
                    crmStatus: leadStatus,
                };
                break;

            case 'callNode':
                updatedData = {
                    label,
                    promptId: data.promptId,
                    promptName: data.promptName,
                    aiPrompt: data.promptName,
                    callText: data.callText,
                    leaveVoicemail: data.leaveVoicemail,
                    subLabel: `AI Prompt: ${data.promptName}`,
                    tagsType: data.workflowTagsType,
                } as Partial<CallNodeData>;
                break;

            case 'smsNode': {
                const userUnit = data.userAbandonedTimeoutUnit;
                const agentUnit = data.agentAbandonedTimeoutUnit;
                updatedData = {
                    label,
                    smsMode: data.smsMode,
                    messageContent: data.messageContent,
                    langfusePromptId: data.langfusePromptId,
                    userAbandonedTimeoutUnit: userUnit,
                    agentAbandonedTimeoutUnit: agentUnit,
                    userAbandonedTimeoutHours: userUnit === 'hours' ? data.userAbandonedTimeoutValue : undefined,
                    agentAbandonedTimeoutHours: agentUnit === 'hours' ? data.agentAbandonedTimeoutValue : undefined,
                    userAbandonedTimeoutMinutes: userUnit === 'minutes' ? data.userAbandonedTimeoutValue : undefined,
                    agentAbandonedTimeoutMinutes: agentUnit === 'minutes' ? data.agentAbandonedTimeoutValue : undefined,
                    expectResponse: data.expectResponse,
                    subLabel: data.smsMode === 'static' ? 'Static' : 'Agentic',
                } as Partial<SMSNodeData>;
                break;
            }

            case 'emailNode':
                updatedData = {
                    label,
                    messageContent: data.messageContent,
                };
                break;

            case 'delayNode':
                if (delayType === 'TIMESTAMP') {
                    updatedData = {
                        label,
                        delayType: 'TIMESTAMP',
                        specificDateTime,
                        timeValue: '30',
                        timeUnit: 'minutes',
                        subLabel: `At: ${new Date(specificDateTime).toLocaleString()}`,
                    } as Partial<DelayNodeData>;
                } else {
                    updatedData = {
                        label,
                        delayType: 'DURATION',
                        timeValue,
                        timeUnit,
                        specificDateTime: '',
                        subLabel: `Wait: ${timeValue} ${timeUnit}`,
                    } as Partial<DelayNodeData>;
                }
                break;

            case 'decisionNode': {
                const decisionData = decisionConfigRef.current?.getData();
                if (decisionData) {
                    updatedData = {
                        label,
                    } as Partial<DecisionNodeData>;
                    onSave(node.id, updatedData);
                    if (onDecisionDone) {
                        const validRows = decisionData.conditionRows.filter((r) => r.values.length > 0);
                        onDecisionDone(node.id, validRows, decisionData.defaultNextNodeType);
                    }
                    return;
                }
                updatedData = { label };
                break;
            }

            default:
                updatedData = {
                    label,
                };
                break;
        }

        onSave(node.id, updatedData);
    };

    // Get node-specific fields based on node type
    const renderNodeSpecificFields = () => {
        switch (node.type) {
            case 'leadNode':
                return <NodeConfigLead value={leadStatus} onChange={setLeadStatus} />;

            case 'callNode':
                return <NodeConfigCall isLoadingPrompts={isLoadingPrompts} prompts={prompts} />;

            case 'smsNode':
                return <NodeConfigSms isLoadingPrompts={isLoadingPrompts} prompts={prompts} />;

            case 'emailNode':
                return (
                    <div>
                        <TextArea
                            id='message-content'
                            label='Message Content'
                            rows={4}
                            {...form.register('messageContent')}
                        />
                    </div>
                );

            case 'delayNode':
                return (
                    <NodeConfigDelay
                        delayType={delayType}
                        timeValue={timeValue}
                        timeUnit={timeUnit}
                        specificDateTime={specificDateTime}
                        onDelayTypeChange={setDelayType}
                        onTimeValueChange={setTimeValue}
                        onTimeUnitChange={setTimeUnit}
                        onSpecificDateTimeChange={setSpecificDateTime}
                    />
                );

            case 'decisionNode':
                return (
                    <NodeConfigDecision
                        ref={decisionConfigRef}
                        node={node as Node<DecisionNodeData>}
                        nodes={nodes}
                        terminalDecisionNodeId={terminalDecisionNodeId}
                    />
                );

            default:
                return null;
        }
    };

    return (
        <FormProvider {...form}>
            <Modal
                isOpen={true}
                onClose={onClose}
                title={`Edit ${getNodeTypeLabel(node)} Node`}
                size={node.type === 'decisionNode' ? 'lg' : 'md'}
                onDone={form.handleSubmit(handleSave)}
                doneText='Save Changes'
            >
                <div className='flex flex-col gap-xl'>
                    <Input
                        label='Label'
                        type='text'
                        placeholder={`Enter ${getNodeTypeLabel(node)} label`}
                        {...form.register('label')}
                    />

                    {renderNodeSpecificFields()}
                </div>
            </Modal>
        </FormProvider>
    );
};

export default NodeConfigModal;
